import { useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Sezione "Non trovi il tuo cimitero?" + modulo di richiesta: il cliente può
// inviare la richiesta senza acquistare nulla e senza interrompere l'acquisto.
export default function CemeteryRequest({ testid = "cemetery-request" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", surname: "", phone: "", email: "", comune: "", cemetery_name: "", message: "", privacy: false });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const openModal = () => {
    setForm((f) => ({
      ...f,
      name: f.name || user?.name || "",
      surname: f.surname || user?.surname || "",
      email: f.email || user?.email || "",
      phone: f.phone || user?.phone || "",
    }));
    setSent(false);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy) {
      toast.error("Per inviare la richiesta serve il consenso al trattamento dei dati.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/cemetery-requests", form);
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invio non riuscito. Riprova tra poco.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border-2 border-dashed border-[#c9d7cd] bg-[#f4f8f5] p-5" data-testid={`${testid}-block`}>
      <p className="font-bold text-navy uppercase tracking-wide text-sm mb-1">Non trovi il tuo cimitero?</p>
      <p className="text-sm text-body mb-4">
        Stiamo ampliando il servizio. Segnalaci il tuo cimitero e ti contatteremo per spiegarti come possiamo aiutarti.
      </p>
      <button type="button" onClick={openModal} data-testid={`${testid}-open-btn`} className="btn-secondary font-bold">
        Richiedi il tuo cimitero
      </button>

      {open && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={() => setOpen(false)} data-testid={`${testid}-modal`}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 mb-5">
              <h3 className="font-serif text-2xl text-navy">Richiedi il tuo cimitero</h3>
              <button type="button" onClick={() => setOpen(false)} data-testid={`${testid}-close`} className="p-2 text-muted-soft hover:text-navy" aria-label="Chiudi">
                <X size={22} />
              </button>
            </div>
            {sent ? (
              <div className="text-center py-6 space-y-3" data-testid={`${testid}-success`}>
                <p className="font-serif text-2xl text-navy">Richiesta ricevuta!</p>
                <p className="text-sm text-body leading-relaxed">
                  Abbiamo registrato la tua richiesta. Ti contatteremo per darti maggiori informazioni sul servizio.
                </p>
                <button type="button" onClick={() => setOpen(false)} className="btn-primary mt-2">Chiudi</button>
              </div>
            ) : (
              <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="label-soft" htmlFor={`${testid}-name`}>Nome *</label>
                  <input id={`${testid}-name`} data-testid={`${testid}-name`} className="input-soft w-full" value={form.name} onChange={set("name")} required />
                </div>
                <div>
                  <label className="label-soft" htmlFor={`${testid}-surname`}>Cognome *</label>
                  <input id={`${testid}-surname`} data-testid={`${testid}-surname`} className="input-soft w-full" value={form.surname} onChange={set("surname")} required />
                </div>
                <div>
                  <label className="label-soft" htmlFor={`${testid}-phone`}>Numero di telefono *</label>
                  <input id={`${testid}-phone`} data-testid={`${testid}-phone`} type="tel" className="input-soft w-full" value={form.phone} onChange={set("phone")} required />
                </div>
                <div>
                  <label className="label-soft" htmlFor={`${testid}-email`}>Email *</label>
                  <input id={`${testid}-email`} data-testid={`${testid}-email`} type="email" className="input-soft w-full" value={form.email} onChange={set("email")} required />
                </div>
                <div>
                  <label className="label-soft" htmlFor={`${testid}-comune`}>Comune *</label>
                  <input id={`${testid}-comune`} data-testid={`${testid}-comune`} className="input-soft w-full" value={form.comune} onChange={set("comune")} required />
                </div>
                <div>
                  <label className="label-soft" htmlFor={`${testid}-cemetery`}>Nome del cimitero *</label>
                  <input id={`${testid}-cemetery`} data-testid={`${testid}-cemetery`} className="input-soft w-full" value={form.cemetery_name} onChange={set("cemetery_name")} required />
                </div>
                <div className="sm:col-span-2">
                  <label className="label-soft" htmlFor={`${testid}-message`}>Messaggio o richiesta aggiuntiva (facoltativo)</label>
                  <textarea id={`${testid}-message`} data-testid={`${testid}-message`} rows={3} className="input-soft w-full" value={form.message} onChange={set("message")} />
                </div>
                <label className="sm:col-span-2 flex items-start gap-3 rounded-xl border border-[#e2e8f0] bg-white p-3 cursor-pointer select-none" data-testid={`${testid}-privacy-label`}>
                  <input type="checkbox" data-testid={`${testid}-privacy`} checked={form.privacy} onChange={set("privacy")} className="mt-0.5 h-5 w-5 shrink-0 accent-[#4a7c59]" />
                  <span className="text-xs text-body leading-relaxed">
                    Acconsento al trattamento dei miei dati personali per essere ricontattato/a in merito a questa
                    richiesta, nel rispetto della normativa privacy (GDPR). I dati non verranno usati per altre finalità.
                  </span>
                </label>
                <button type="submit" disabled={busy} data-testid={`${testid}-submit`} className="btn-primary sm:col-span-2 disabled:opacity-60">
                  {busy ? "Invio in corso…" : "Invia la richiesta"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
